import React, { useRef, useState, useEffect } from "react";
import { ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import { assetUrl } from "../../lib/api";

/**
 * Plan image with clickable pins positioned by normalised (x,y) 0..1 coords.
 * - Public: pass pins + onPinClick.
 * - Admin edit: pass editable + onPlace(x,y) to drop/move a pin by clicking the image.
 * Pins are children of the zoomed element, so their % positions stay accurate at any zoom.
 */
const HotspotPlan = ({ src, pins = [], onPinClick, editable = false, onPlace, height = "h-[70vh]", selectedId, marker, region, fit = false, pois = [], zones = [], onPoiClick, routes = [], maxHeight = null }) => {
  const imgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    setLoaded(false);
    // If the image is already cached/complete, onLoad may never fire — detect it.
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, [src]);
  const clamp = (z) => Math.min(4, Math.max(1, z));
  const fitContain = fit && zoom === 1 && !!maxHeight;

  const handleImgClick = (e) => {
    if (!editable || !onPlace) return;
    const rect = imgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    if (x >= 0 && x <= 1 && y >= 0 && y <= 1) onPlace(Number(x.toFixed(4)), Number(y.toFixed(4)));
  };

  if (!src) return null;

  return (
    <div className="relative rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0C1510]">
      {fit && !loaded && (
        <div className="absolute inset-0 z-[5] flex items-center justify-center bg-[#0C1510]" data-testid="plan-loading">
          <span className="animate-pulse text-[#F3EEE3]/40 text-sm">Loading plan…</span>
        </div>
      )}
      <div className={`w-full ${fit ? (zoom > 1 ? "overflow-auto" : (fitContain ? "overflow-hidden flex justify-center items-start" : "overflow-hidden")) + (loaded ? "" : " min-h-[42vh]") : `${height} overflow-auto`}`} data-testid="hotspot-plan">
        <div className={`relative ${fitContain ? "inline-block" : ""}`} style={fitContain ? { maxHeight } : { width: `${zoom * 100}%` }}>
          <img
            ref={imgRef}
            src={assetUrl(src)}
            alt="Stage plan"
            draggable={false}
            onClick={handleImgClick}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            style={fitContain ? { maxHeight } : undefined}
            className={`block ${fitContain ? "max-h-full max-w-full w-auto h-auto" : "w-full h-auto"} ${editable ? "cursor-crosshair" : ""}`}
          />
          {pins.map((p) => (
            <button
              key={p.id}
              onClick={(e) => { e.stopPropagation(); onPinClick && onPinClick(p, e.currentTarget); }}
              data-testid={`plan-pin-${p.label}`}
              title={p.title || `Lot ${p.label}`}
              style={{ left: `${p.x * 100}%`, top: `${p.y * 100}%`, background: p.color || "#C9B79C" }}
              className={`absolute -translate-x-1/2 -translate-y-1/2 min-w-[26px] h-[26px] px-1.5 rounded-full text-[11px] font-semibold text-[#12201A] flex items-center justify-center shadow-[0_2px_8px_rgba(0,0,0,0.5)] ring-2 transition-transform hover:scale-125 ${selectedId === p.id ? "ring-white scale-125 z-20" : "ring-white/70"}`}
            >
              {p.label}
            </button>
          ))}
          {routes.length > 0 && (
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-[18]" viewBox="0 0 100 100" preserveAspectRatio="none" data-testid="plan-routes">
              <defs>
                {routes.map((rt, ri) => (
                  <marker key={ri} id={`plan-arrow-${ri}`} markerWidth="7" markerHeight="7" refX="3.2" refY="3" orient="auto">
                    <path d="M0,0 L6.4,3 L0,6 Z" fill={rt.color || "#C9B79C"} />
                  </marker>
                ))}
              </defs>
              {routes.map((rt, ri) => (
                <polyline
                  key={ri}
                  points={rt.points.map(([x, y]) => `${x * 100},${y * 100}`).join(" ")}
                  fill="none"
                  stroke={rt.color || "#C9B79C"}
                  strokeWidth={rt.width || 1}
                  strokeDasharray={rt.dash || "2.6 1.7"}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  markerEnd={rt.arrow ? `url(#plan-arrow-${ri})` : undefined}
                  style={{ filter: "drop-shadow(0 0 1.6px rgba(0,0,0,0.7))" }}
                >
                  <animate attributeName="stroke-dashoffset" from="0" to="-17" dur="1s" repeatCount="indefinite" />
                </polyline>
              ))}
            </svg>
          )}
          {routes.filter((rt) => rt.label).map((rt, ri) => {
            const [lx, ly] = rt.points[rt.points.length - 1];
            return (
              <div key={`rl-${ri}`} style={{ left: `${lx * 100}%`, top: `${ly * 100}%` }} className="absolute -translate-x-1/2 -translate-y-[190%] z-[19] pointer-events-none">
                <span className="whitespace-nowrap bg-[#12201A]/92 text-[#C9B79C] text-[10px] font-semibold px-2 py-0.5 rounded-md border border-[#C9B79C]/60 shadow">{rt.label}</span>
              </div>
            );
          })}
          {marker && (
            <div style={{ left: `${marker.x * 100}%`, top: `${marker.y * 100}%` }} className="absolute -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none" data-testid="area-marker">
              <div className="relative flex flex-col items-center">
                <span className="absolute bottom-full mb-2 whitespace-nowrap bg-[#12201A] text-[#F3EEE3] text-[11px] font-semibold px-2.5 py-1 rounded-full border border-[#C9B79C] shadow-[0_2px_10px_rgba(0,0,0,0.6)]">{marker.label}</span>
                <span className="relative flex h-5 w-5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9B79C] opacity-70" />
                  <span className="relative inline-flex rounded-full h-5 w-5 bg-[#C9B79C] border-2 border-white" />
                </span>
              </div>
            </div>
          )}
          {region && (
            <div
              style={{ left: `${region.x * 100}%`, top: `${region.y * 100}%`, width: `${region.w * 100}%`, height: `${region.h * 100}%` }}
              className="absolute z-20 pointer-events-none rounded-2xl border-2 border-dashed border-[#C9B79C] bg-[#C9B79C]/12 shadow-[0_0_0_9999px_rgba(12,21,16,0.42)]"
              data-testid="area-region"
            >
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap bg-[#12201A] text-[#F3EEE3] text-[11px] font-semibold px-3 py-1 rounded-full border border-[#C9B79C] shadow-[0_2px_10px_rgba(0,0,0,0.6)]">{region.label}</span>
            </div>
          )}
          {pois.map((poi, i) => {
            const Icon = poi.icon;
            return (
              <button
                key={i}
                type="button"
                data-testid={`poi-${i}`}
                onClick={(e) => { e.stopPropagation(); onPoiClick && onPoiClick(poi, e.currentTarget); }}
                style={{ left: `${poi.x * 100}%`, top: `${poi.y * 100}%` }}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-30 flex items-center gap-1.5 group cursor-pointer"
              >
                <span className="shrink-0 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-[0_2px_8px_rgba(0,0,0,0.6)] transition-transform group-hover:scale-125" style={{ background: poi.color || "#3E7A5E" }}>
                  {Icon ? <Icon size={13} className="text-white" /> : null}
                </span>
                <span className="absolute left-[135%] top-1/2 -translate-y-1/2 whitespace-nowrap bg-[#12201A]/95 text-[#F3EEE3] text-[10px] font-semibold px-2 py-0.5 rounded-md border border-[#C9B79C]/70 shadow pointer-events-none opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 z-40">{poi.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="absolute bottom-3 right-3 flex flex-col gap-1.5">
        <button onClick={() => setZoom((z) => clamp(z + 0.4))} data-testid="hotspot-zoom-in" className="w-9 h-9 rounded-lg bg-[#12201A]/85 border border-[#F3EEE3]/15 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><ZoomIn size={16} /></button>
        <button onClick={() => setZoom((z) => clamp(z - 0.4))} data-testid="hotspot-zoom-out" className="w-9 h-9 rounded-lg bg-[#12201A]/85 border border-[#F3EEE3]/15 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><ZoomOut size={16} /></button>
        <button onClick={() => setZoom(1)} className="w-9 h-9 rounded-lg bg-[#12201A]/85 border border-[#F3EEE3]/15 text-[#F3EEE3] flex items-center justify-center hover:bg-[#12201A]"><Maximize2 size={15} /></button>
      </div>
      {editable && (
        <div className="absolute top-3 left-3 text-[0.62rem] text-[#F3EEE3]/80 bg-[#12201A]/80 rounded-full px-3 py-1.5 pointer-events-none">
          Click on the plan to place the selected lot
        </div>
      )}
    </div>
  );
};

export default HotspotPlan;
